import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { selectAnswer } from "../../store/selectors/answerSelectors";
import { resetFilters, setGlobalFilterValue } from "../../store/slices/answerSlice";
import { ChangeEvent, ReactNode } from "react";
import itemsIcon from "../../assets/items-icon.svg";

type Props = {
    /** Row count after filtering — pass the table's own filtered set, since
     *  responses differ between versions and filters can match differently. */
    filteredCount: number;
    totalCount: number;
    /** Optional action(s) (e.g. the Calculate EEXI and CII button). */
    actions?: ReactNode;
};

// A column/global filter is active only when its meta carries a non-empty value.
const isFilterActive = (meta: unknown): boolean => {
    const value = meta && typeof meta === "object" ? (meta as { value?: unknown }).value : undefined;
    return value != null && value !== "";
};

/**
 * Shared header for both Checklist Review tables (editable and surveyor
 * submission): global search, table reset and a question count. Search and
 * reset act on the shared filter/sort state, so they apply to both tabs.
 */
export const ReviewTableHeader = ({ filteredCount, totalCount, actions }: Props) => {
    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();
    const { globalFilterValue, filters } = useAppSelector(selectAnswer);

    const onGlobalFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(setGlobalFilterValue(e.target.value));
    };

    const onReset = () => {
        dispatch(resetFilters());
    };

    // Only show "X of Y" when a filter/search is actually active. On first load
    // the full and filtered counts settle at different times (and conditional
    // visibility grows the row count as answers arrive), so a count mismatch
    // alone would briefly imply a filter that isn't there.
    const hasActiveFilter = Object.values(filters).some(isFilterActive);
    const countLabel = hasActiveFilter && filteredCount !== totalCount
        ? t("answers.header.questionCountFiltered", { filtered: filteredCount, total: totalCount })
        : t("answers.header.questionCount", { total: totalCount });

    return (
        <div className="flex flex-wrap align-items-center gap-3 mb-3">
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText
                    value={globalFilterValue}
                    onChange={onGlobalFilterChange}
                    placeholder={t("answers.table.search")}
                />
            </span>
            <Button
                label={t("answers.header.resetTable")}
                outlined
                onClick={onReset}
                className="reset-table-button"
                // Inline: the theme strips outlined-button borders (border: 0)
                // and re-colours via currentColor, so class-based overrides
                // keep losing the cascade. Box-shadow states live in App.css.
                style={{ border: "1px solid #BBBFC3", color: "#007E8E", background: "transparent" }}
            />
            <span className="question-count flex align-items-center gap-2">
                <img src={itemsIcon} alt="" />
                {countLabel}
            </span>
            {actions && <div className="ml-auto">{actions}</div>}
        </div>
    );
};
